import { Component, type ReactNode, type ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-bg-secondary p-4">
          <div className="w-full max-w-lg rounded-lg bg-bg p-8 shadow-lg">
            <h1 className="mb-2 text-xl font-bold text-error">
              Something went wrong
            </h1>
            <p className="mb-4 text-sm text-text-secondary">
              The application encountered an unexpected error. Try refreshing the page or clearing your browser data.
            </p>
            <pre className="mb-4 max-h-40 overflow-auto rounded bg-bg-tertiary p-3 text-xs text-text-primary">
              {this.state.error?.message}
            </pre>
            <div className="flex gap-3">
              <button
                onClick={() => window.location.reload()}
                className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Reload Page
              </button>
              <button
                onClick={() => {
                  sessionStorage.clear();
                  window.location.href = '/';
                }}
                className="rounded bg-bg-tertiary px-4 py-2 text-sm font-medium text-text-secondary hover:bg-border"
              >
                Clear Session &amp; Retry
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
